import 'dayjs/locale/sv';

import dayjs from 'dayjs';

import { Button, Typography } from '@/components';
import { useTranslation } from 'react-i18next';
import { usePayoutRequest } from '@/hooks/influencer';
import { prettifyNumber } from '@/utils/prettifyNumber';
import { TranslatedLink } from '@/utils/translatedComponents';

import styles from './Payout.module.css';

interface PayoutProps {
  payout: Api.Influencer.Payout;
  onSuccess: TypeAny;
}

function getDaysUntil(targetDate: DateAsString = dayjs()) {
  const today = dayjs();

  return dayjs(targetDate).diff(today, 'day');
}

function getCountdownMessage(daysUntil: number, t: TypeAny) {
  if (daysUntil > 1) {
    return `${daysUntil} ${t('payout.daysLeft')}`;
  }

  if (daysUntil === 1) {
    return t('payout.daysTomorrow');
  }

  return t('payout.checkEmail');
}

export function Payout({ payout, onSuccess }: PayoutProps) {
  const { t } = useTranslation();

  const { mutate: requestPayout, isLoading } = usePayoutRequest({
    collaborationId: payout?.collaboration_id,
    onSuccess
  });

  return (
    <div className={styles.payout}>
      <div className={styles.payoutCampaign}>
        <Typography sizes={['h5']}>
          <TranslatedLink href={`/connect/collaborations/${payout.collaboration_id}`}>
            {payout.campaign_name}
          </TranslatedLink>
        </Typography>

        <Typography sizes={['body-small']}>
          {payout.date_start &&
            payout.date_end &&
            `${dayjs(payout.date_start).format('YYYY-MM-DD')} - ${dayjs(payout.date_end).format('YYYY-MM-DD')}`}
        </Typography>
      </div>

      <div className={styles.payoutCta}>
        <Typography sizes={['body', 'bold']}>
          {`${prettifyNumber(payout.amount)} ${payout.currency || '-'}`}
        </Typography>

        <Button
          disabled={payout.status !== 'CAN_REQUEST_PAYOUT'}
          isLoading={isLoading}
          onClick={requestPayout}
          theme="cta"
        >
          {(payout.date_requested &&
            payout.status !== 'PAID' &&
            getCountdownMessage(getDaysUntil(dayjs(payout.date_requested).add(45, 'days')), t)) ||
            t(`payout.status.${payout.status}`)}
        </Button>
      </div>
    </div>
  );
}
